import {
  fetchTemplatePreview,
  updateTemplateStatus,
  updateTemplateCategory,
  deleteTemplate,
} from "../../../service/settings";

export const handlePreviewUtil = async ({
  template,
  setIsLoading,
  setTemplatePreview,
  setOpenPreview,
  handleToastMessage,
}) => {
  if (!template) return;

  setIsLoading(true);
  try {
    const response = await fetchTemplatePreview(
      template?.formstack_id,
      template?.key
    );

    if (response?.status === 200) {
      const data = response?.data?.data;

      const responseData = {
        file_content: data?.file_content || data?.base64_content,
        file_type: data?.file_type,
        content_type: data?.content_type,
        filename: data?.template_name,
      };

      setTemplatePreview(responseData);
      setOpenPreview(true);
    } else {
      handleToastMessage("Failed to fetch template preview.");
    }
  } catch (error) {
    console.error("Preview error:", error);
    handleToastMessage("Something went wrong while fetching preview.");
  } finally {
    setIsLoading(false);
  }
};

export const handleDisableTemplateUtil = async ({
  template,
  selectedCategory,
  handleCategoryClick,
  handleToastMessage,
}) => {
  try {
    const newStatus = !template.is_active;
    await updateTemplateStatus(template?.formstack_id, newStatus);
    await handleCategoryClick(selectedCategory);
  } catch (err) {
    console.error("Failed to update status", err);
    handleToastMessage("Failed to update template status. Try again.");
  }
};

export const handleDeleteTemplateUtil = async ({
  template,
  selectedCategory,
  handleCategoryClick,
  handleToastMessage,
  setIsLoading,
}) => {
  if (!template) return;
  setIsLoading(true);
  try {
    await deleteTemplate(template.formstack_id);
    await handleCategoryClick(selectedCategory);
    handleToastMessage("Template deleted successfully", true);
  } catch (err) {
    console.error("Failed to delete template", err);
    handleToastMessage(
      err?.response?.data?.error || "Failed to delete template. Try again."
    );
  } finally {
    setIsLoading(false);
  }
};

export const handleMoveTemplateCategoryUtil = async ({
  template,
  destinationCategoryCode,
  selectedCategory,
  handleCategoryClick,
  handleToastMessage,
  setIsLoading,
  setMoveCategoryModal,
  setTemplatesByCategory,
}) => {
  if (!template?.formstack_id) {
    handleToastMessage("This template cannot be moved.");
    return;
  }
  if (!destinationCategoryCode) {
    handleToastMessage("Select a destination category.");
    return;
  }
  if (destinationCategoryCode === selectedCategory) {
    handleToastMessage("Template is already in that category.");
    return;
  }

  setIsLoading(true);
  try {
    await updateTemplateCategory(template.formstack_id, destinationCategoryCode);
    setMoveCategoryModal?.(null);
    setTemplatesByCategory?.((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      delete next[destinationCategoryCode];
      return next;
    });
    await handleCategoryClick(selectedCategory);
    handleToastMessage("Template moved successfully", true);
  } catch (err) {
    console.error("Failed to move template", err);
    handleToastMessage(
      err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to move template. Try again."
    );
  } finally {
    setIsLoading(false);
  }
};

export const handleCreateCategoryUtil = async ({
  categoryName,
  addNewCategory,
  loadInitialTemplates,
  handleToastMessage,
  setAddCategoryModal,
  setIsLoading,
  setCategoryName,
  setError,
  scrollToElement = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }
  },
}) => {
  if (!categoryName.trim()) {
    setError("Category name cannot be empty"); // or ERROR_MESSAGES.CATEGORY_NAME_EMPTY
    return;
  }
  if (categoryName.length > 50) {
    setError("Category name cannot exceed 50 characters"); // or ERROR_MESSAGES.CATEGORY_NAME_MAX_LENGTH
    return;
  }

  setError("");
  setIsLoading(true);

  const categoryCode = categoryName.trim().toUpperCase().replaceAll(/\s+/g, "_");

  try {
    await addNewCategory(categoryCode, categoryName.trim());
    setAddCategoryModal(false);
    await loadInitialTemplates(categoryCode);
    handleToastMessage("Category added successfully", true);
    setCategoryName("");
    setTimeout(() => scrollToElement(categoryCode), 0);
  } catch (err) {
    console.error("Error adding category:", err);
    const message = err?.response?.data?.message || "Failed to add category";
    handleToastMessage(message);
  } finally {
    setIsLoading(false);
  }
};

export const handleUpdateCategoryUtil = async ({
  categoryName,
  selectedCategoryObj,
  updateCategory,
  loadInitialTemplates,
  handleToastMessage,
  setAddCategoryModal,
  setIsLoading,
  setCategoryName,
  setError,
}) => {
  if (!selectedCategoryObj?.id) {
    handleToastMessage("Select a category before editing.");
    return;
  }
  if (!categoryName.trim()) {
    setError("Category name cannot be empty");
    return;
  }
  if (categoryName.length > 50) {
    setError("Category name cannot exceed 50 characters");
    return;
  }

  setError("");
  setIsLoading(true);

  try {
    await updateCategory(selectedCategoryObj.id, {
      name: categoryName.trim(),
    });
    setAddCategoryModal(false);
    await loadInitialTemplates(selectedCategoryObj.key);
    handleToastMessage("Category updated successfully", true);
    setCategoryName("");
  } catch (err) {
    console.error("Error updating category:", err);
    const message =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      "Failed to update category";
    handleToastMessage(message);
  } finally {
    setIsLoading(false);
  }
};

export const handleDeleteCategoryUtil = async ({
  selectedCategoryObj,
  deleteCategory,
  loadInitialTemplates,
  handleToastMessage,
  setIsLoading,
  setDeleteCategoryModal,
}) => {
  if (!selectedCategoryObj?.id) {
    handleToastMessage("Select a category before deleting.");
    return;
  }
  if (Number(selectedCategoryObj.count) > 0) {
    handleToastMessage("Delete the templates before deleting this category.");
    return;
  }

  setIsLoading(true);

  try {
    await deleteCategory(selectedCategoryObj.id);
    setDeleteCategoryModal(null);
    await loadInitialTemplates();
    handleToastMessage("Category deleted successfully", true);
  } catch (err) {
    console.error("Error deleting category:", err);
    const message =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      "Failed to delete category";
    handleToastMessage(message);
  } finally {
    setIsLoading(false);
  }
};
